import { KITS, MATCH_MODES } from "@golazo/shared";
import { t } from "../i18n/index.js";
import {
  acceptOffer,
  answerMessage,
  canRetire,
  renamePlayer,
  careerOverall,
  matchdayOf,
  mustRetire,
  newCareer,
  nextFixture,
  resolvePending,
  retire,
  startingClubs,
  tableOf,
  type CareerEvent,
  type HallOfFameEntry,
} from "../career/career.js";
import { addToHallOfFame, loadHallOfFame } from "../career/hallOfFame.js";
import { careerMatchSetup } from "../career/matchSetup.js";
import {
  ATTRIBUTE_KEYS,
  CLUBS_PER_DIVISION,
  DIVISIONS,
  MATCHDAYS,
  MOVERS,
  TOP_TIER,
  type AttributeKey,
  type Career,
  type Club,
  type Foot,
  type Position,
} from "../career/model.js";
import { OFFER_PITCHES, STYLE_BLURB, STYLE_LABEL } from "../career/phone.js";
import { objectiveKey } from "../career/phoneTalk.js";
import { loadCareer, saveCareer } from "../career/store.js";
import { claimCareerUnlocks, unlockStore } from "../career/unlocks.js";
import { clubById } from "../career/world.js";
import { loadNickname } from "../online/nickname.js";
import { escapeHtml, kitTokenHtml, ordinal } from "./careerText.js";
import {
  phoneChatHtml,
  phoneInboxHtml,
  phoneUnread,
  type PhoneUi,
  type SeenMap,
  type ThreadId,
} from "./careerPhone.js";
import { confirmDialog } from "./confirmDialog.js";
// Los patrones de las fichas (`.kit-token--*`) viven ahi.
import "./customize.css";
import "./careerHub.css";

/**
 * La pantalla de la carrera. Todo lo que pasa entre partido y partido:
 *
 *  - crear tu jugador y elegir tu primer club (uno de los 8 del barrio);
 *  - la portada: tu ficha, el proximo partido y el boton de jugar;
 *  - cuatro pestañas: el movil, la tabla, el calendario y tu ficha;
 *  - el fin de temporada: las ofertas de tu agente, quedarte o retirarte;
 *  - la retirada y el salon de la fama.
 *
 * Lleva la carrera ella sola (la carga, la avanza y la guarda). A quien la
 * abre solo le devuelve "a jugar" o "atras": montar el partido es cosa de
 * `main.ts`.
 */
export type CareerHubAction = { type: "play" } | { type: "back" };

export interface CareerHubHandle {
  show(opts?: { create?: boolean }): Promise<CareerHubAction>;
  /** La carrera ha cambiado por fuera (llego de la cuenta): se vuelve a pintar. */
  refresh(): void;
  hide(): void;
  /** Salir porque se va a otro sitio desde fuera (un aviso de un amigo). */
  leave(): void;
  dispose(): void;
}

type View = "career" | "create" | "hall";
type Tab = "phone" | "table" | "calendar" | "card";
const TABS: readonly Tab[] = ["phone", "table", "calendar", "card"];

/** Ultimo mensaje leido del movil y la pestaña abierta: comodidades de este navegador. */
/** Hasta donde has leido cada conversacion del movil (`SeenMap` en JSON). */
const SEEN_KEY = "fynta.career.threads";
const TAB_KEY = "fynta.career.tab";

const POSITION_LABEL: Record<Position, string> = { DEL: "Delantero", MED: "Medio", DEF: "Defensa" };
const POSITION_SHORT: Record<Position, string> = { DEL: "DEL", MED: "MED", DEF: "DEF" };
const POSITION_BLURB: Record<Position, string> = {
  DEL: "Si marcas, eres un genio. Si no, un problema.",
  MED: "Corres por todos y la foto se la lleva otro.",
  DEF: "Nadie se acuerda de ti hasta que fallas.",
};
const FOOT_LABEL: Record<Foot, string> = { right: "Diestro", left: "Zurdo" };
const ATTRIBUTE_LABEL: Record<AttributeKey, string> = {
  pace: "Velocidad",
  shooting: "Disparo",
  passing: "Pase",
  stamina: "Fondo",
};

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Comodidad, no estado: sin almacenamiento se vuelve a lo de siempre.
  }
}

function newSeed(): number {
  return (Date.now() ^ Math.floor(Math.random() * 0x7fffffff)) >>> 0;
}

/** El apodo, salvo que sea el "sin nombre" que pone el servidor (Jugador1234). */
function defaultName(): string {
  const nick = loadNickname().trim();
  return /^(jugador|player|invitado|guest)\d*$/i.test(nick) ? "" : nick.slice(0, 16);
}

const kitOf = (club: Club) => KITS[club.kitId];

/** La frase de la barra del menu principal: donde esta tu carrera. */
export function seasonTeaser(): string {
  const c = loadCareer();
  if (!c) return t("De los 17 a los 35. Del barrio a Primera.");
  if (c.phase === "retired") return t("Carrera terminada. El salón de la fama te espera.");
  const club = c.clubs.find((x) => x.id === c.season.club)?.name ?? "";
  if (c.phase === "offseason") return t("{club} · Fin de temporada", { club });
  return t("{club} · {division} · Jornada {n} de {total}", {
    club,
    division: t(DIVISIONS[c.season.tier]!.name),
    n: c.season.matchday + 1,
    total: MATCHDAYS,
  });
}

interface CreateDraft {
  seed: number;
  name: string;
  number: number;
  position: Position;
  foot: Foot;
  clubId: string | null;
}

export function createCareerHub(root: HTMLElement = document.body): CareerHubHandle {
  const overlay = document.createElement("div");
  overlay.id = "season-screen";
  overlay.className = "ch is-hidden";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-label", t("Carrera"));
  root.appendChild(overlay);

  let career: Career | null = null;
  let view: View = "career";
  let tab: Tab = (TABS as readonly string[]).includes(readStored(TAB_KEY) ?? "") ? (readStored(TAB_KEY) as Tab) : "phone";
  let tableTier = 0;
  let flash: string[] = [];
  let draft: CreateDraft | null = null;
  let createError = "";
  let resolveShow: ((a: CareerHubAction) => void) | null = null;
  let busy = false;

  const seenMap = (): SeenMap => {
    const stored = readStored(SEEN_KEY);
    if (stored === null) {
      // De antes de las conversaciones habia un solo "leido hasta": vale para todas.
      const legacy = Number(readStored("fynta.career.seen") ?? "0") || 0;
      const all: ThreadId[] = ["coach", "squad", "agent", "mom", "rival", "press", "fans", "system"];
      const map: SeenMap = Object.fromEntries(all.map((th) => [th, legacy]));
      writeStored(SEEN_KEY, JSON.stringify(map));
      return map;
    }
    try {
      const raw = JSON.parse(stored) as unknown;
      return raw && typeof raw === "object" ? (raw as SeenMap) : {};
    } catch {
      return {};
    }
  };
  const markThreadSeen = (thread: ThreadId, id: number): void => {
    const map = seenMap();
    if ((map[thread] ?? 0) >= id) return;
    map[thread] = id;
    writeStored(SEEN_KEY, JSON.stringify(map));
  };
  /** El movil: bandeja o conversacion abierta, y lo que se esta "escribiendo". */
  let phoneUi: PhoneUi = { thread: null, typing: null, effect: null };
  /** Lo leido de la conversacion abierta ANTES de abrirla: lo de despues entra animado. */
  let chatSeenBefore = 0;
  let typingTimer = 0;

  // ------------------------------------------------------------ avanzar

  /** Guarda, pasa lo ganado al almacen de equipaciones y lo celebra arriba. */
  const commit = (next: Career, events: readonly CareerEvent[] = []): void => {
    career = next;
    saveCareer(next);
    const unlocked = claimCareerUnlocks(unlockStore(), next);
    for (const kitId of unlocked.kits) {
      const label = KITS[kitId] ? t(KITS[kitId]!.label) : kitId;
      flash.push(`${kitTokenHtml(KITS[kitId], "", "s")} ${escapeHtml(t("Equipación nueva: {kit}", { kit: label }))}`);
    }
    if (unlocked.custom) flash.push(`✂️ ${escapeHtml(t("Ya puedes diseñar tu equipación «A medida».")) }`);
    for (const e of events) {
      if (e.type === "promoted") flash.push(`⬆️ ${escapeHtml(t("¡Ascenso a {division}!", { division: t(DIVISIONS[e.tier]!.name) }))}`);
    }
  };

  const finish = (action: CareerHubAction): void => {
    const done = resolveShow;
    resolveShow = null;
    hide();
    done?.(action);
  };

  // ------------------------------------------------------------ pintar

  const topbar = (title: string, extra = ""): string => `
    <header class="ch-top">
      <button type="button" class="ch-back" data-act="back">‹ ${escapeHtml(t("Volver"))}</button>
      <h1 class="ch-title">${escapeHtml(title)}</h1>
      <div class="ch-top-actions">${extra}</div>
    </header>
    ${flash.length ? `<ul class="ch-flash" role="status">${flash.map((f) => `<li>${f}</li>`).join("")}</ul>` : ""}`;

  const meCard = (c: Career): string => {
    const club = clubById(c.clubs, c.season.club);
    const p = c.player;
    return `
      <section class="ch-card ch-me">
        ${kitTokenHtml(kitOf(club), p.number, "l")}
        <div class="ch-me-body">
          <strong class="ch-me-name">${escapeHtml(p.name)}</strong>
          <span class="ch-me-line">${escapeHtml(t(POSITION_LABEL[p.position]))} · ${escapeHtml(t(FOOT_LABEL[p.foot]))} · ${escapeHtml(t("{n} años", { n: p.age }))}</span>
          <span class="ch-me-club">${escapeHtml(club.name)} · ${escapeHtml(t(DIVISIONS[c.season.tier]!.name))}</span>
        </div>
        <div class="ch-me-ovr" title="${escapeHtml(t("Media"))}">
          <b>${careerOverall(c)}</b>
          <span>${escapeHtml(t("MEDIA"))}</span>
        </div>
      </section>`;
  };

  const nextCard = (c: Career): string => {
    const f = nextFixture(c);
    const setup = careerMatchSetup(c);
    if (!f || !setup) return "";
    const home = clubById(c.clubs, setup.teams.home.clubId);
    const away = clubById(c.clubs, setup.teams.away.clubId);
    const rival = f.opponent;
    const mode = MATCH_MODES[setup.modeId];
    const token = (club: Club, kitId: string): string => kitTokenHtml(KITS[kitId], club.name.charAt(0), "m");
    return `
      <section class="ch-card ch-next">
        <header class="ch-card-head">
          <span>${escapeHtml(t("Jornada {n} de {total}", { n: f.matchday + 1, total: MATCHDAYS }))} · ${escapeHtml(t(DIVISIONS[f.tier]!.name))}</span>
          <em>${escapeHtml(mode.label)}</em>
        </header>
        <div class="ch-vs">
          <span class="ch-vs-team${setup.side === "home" ? " is-me" : ""}">${token(home, setup.teams.home.kitId)}<b>${escapeHtml(home.name)}</b></span>
          <span class="ch-vs-x">VS</span>
          <span class="ch-vs-team${setup.side === "away" ? " is-me" : ""}">${token(away, setup.teams.away.kitId)}<b>${escapeHtml(away.name)}</b></span>
        </div>
        <p class="ch-venue">🏟️ ${escapeHtml(f.stadium.name)} · ${escapeHtml(f.home ? t("En casa") : t("Fuera"))}</p>
        <div class="ch-rival">
          <p><b>${escapeHtml(t(STYLE_LABEL[rival.style]))}.</b> ${escapeHtml(t(STYLE_BLURB[rival.style]))}</p>
          <p class="ch-quirk">${escapeHtml(t(rival.quirk))}</p>
          <p class="ch-rival-people">${escapeHtml(t("Su estrella: {star} · Su míster: {coach}", { star: rival.star, coach: rival.coach }))}</p>
        </div>
        ${
          c.objective && c.objective.season === f.season && c.objective.matchday === f.matchday && !c.objective.result
            ? `<p class="ch-objective">🎯 <b>${escapeHtml(t("Encargo del míster"))}:</b> ${escapeHtml(t(objectiveKey(c.objective), { n: c.objective.target }))}</p>`
            : ""
        }
        <button type="button" class="ch-play" data-act="play">${escapeHtml(t("JUGAR"))}</button>
      </section>`;
  };

  const offseasonCard = (c: Career): string => {
    const s = c.history.at(-1);
    const club = clubById(c.clubs, c.season.club);
    const nextDivision = t(DIVISIONS[c.nextTier]!.name);
    let verdict = "";
    if (s?.champion) verdict = `🏆 ${t("¡Campeones de {division}!", { division: t(DIVISIONS[s.tier]!.name) })}`;
    else if (s?.promoted) verdict = `⬆️ ${t("¡Ascenso a {division}!", { division: nextDivision })}`;
    else if (s?.relegated) verdict = `⬇️ ${t("Descenso a {division}.", { division: nextDivision })}`;
    else if (s) verdict = t("{pos} de {n} en {division}", { pos: ordinal(s.position), n: CLUBS_PER_DIVISION, division: t(DIVISIONS[s.tier]!.name) });

    const forced = mustRetire(c);
    const offers = forced
      ? ""
      : c.offers.length
        ? c.offers
            .map((o) => {
              const oc = clubById(c.clubs, o.club);
              return `
                <li class="ch-offer">
                  ${kitTokenHtml(kitOf(oc), oc.name.charAt(0), "m")}
                  <div class="ch-offer-body">
                    <strong>${escapeHtml(oc.name)}</strong>
                    <span>${escapeHtml(t(DIVISIONS[o.tier]!.name))} · ${escapeHtml(t(STYLE_LABEL[oc.style]))}</span>
                    <q>${escapeHtml(t(OFFER_PITCHES[o.pitch % OFFER_PITCHES.length]!))}</q>
                  </div>
                  <button type="button" class="ch-btn ch-btn--gold" data-act="sign" data-club="${oc.id}">${escapeHtml(t("Fichar"))}</button>
                </li>`;
            })
            .join("")
        : `<li class="ch-offer is-empty">${escapeHtml(t("No ha llamado nadie. Tu agente jura que el móvil tenía batería."))}</li>`;

    return `
      <section class="ch-card ch-offseason">
        <header class="ch-card-head"><span>${escapeHtml(t("Fin de la temporada {n}", { n: c.season.number }))}</span></header>
        <p class="ch-verdict">${escapeHtml(verdict)}</p>
        ${
          s
            ? `<div class="ch-tiles">
                <div><b>${s.points}</b><span>${escapeHtml(t("Puntos"))}</span></div>
                <div><b>${s.goals}</b><span>${escapeHtml(t("Goles"))}</span></div>
                <div><b>${s.assists}</b><span>${escapeHtml(t("Asistencias"))}</span></div>
                <div><b>${s.grade.toFixed(1)}</b><span>${escapeHtml(t("Nota media"))}</span></div>
              </div>`
            : ""
        }
        ${
          forced
            ? `<p class="ch-forced">${escapeHtml(t("Con {age} años no hay contrato que te salve. Toca colgar las botas.", { age: c.player.age }))}</p>`
            : `<h3 class="ch-sub">${escapeHtml(t("Ofertas"))}</h3>
               <ul class="ch-offers">${offers}</ul>
               <button type="button" class="ch-btn ch-btn--wide" data-act="stay">
                 ${escapeHtml(t("Quedarme en el {club}", { club: club.name }))}
                 <small>${escapeHtml(t("La temporada que viene, en {division}", { division: nextDivision }))}</small>
               </button>`
        }
        ${
          forced || canRetire(c)
            ? `<button type="button" class="ch-btn ch-btn--wide ch-btn--retire" data-act="retire">${escapeHtml(t("Colgar las botas"))}</button>`
            : ""
        }
      </section>`;
  };

  const retiredCard = (c: Career): string => {
    const entry = loadHallOfFame().find((e) => e.id.startsWith(`${c.seed}-`));
    return `
      <section class="ch-card ch-retired">
        <header class="ch-card-head"><span>${escapeHtml(t("Fin de la carrera"))}</span></header>
        ${entry ? `<p class="ch-epitaph">«${escapeHtml(t(entry.verdict))}»</p>` : ""}
        <div class="ch-tiles">
          <div><b>${c.history.length}</b><span>${escapeHtml(t("Temporadas"))}</span></div>
          <div><b>${c.totals.matches}</b><span>${escapeHtml(t("Partidos"))}</span></div>
          <div><b>${c.totals.goals}</b><span>${escapeHtml(t("Goles"))}</span></div>
          <div><b>${c.titles.length}</b><span>${escapeHtml(t("Títulos"))}</span></div>
        </div>
        <button type="button" class="ch-play" data-act="new">${escapeHtml(t("NUEVA CARRERA"))}</button>
        <button type="button" class="ch-btn ch-btn--wide" data-act="hall">${escapeHtml(t("Salón de la fama"))}</button>
      </section>`;
  };

  // --- pestañas

  const phoneTab = (c: Career): string =>
    phoneUi.thread ? phoneChatHtml(c, phoneUi.thread, phoneUi, chatSeenBefore) : phoneInboxHtml(c, seenMap());

  const tableTab = (c: Career): string => {
    const chips = DIVISIONS.map(
      (d) =>
        `<button type="button" class="ch-chip${d.tier === tableTier ? " is-on" : ""}" data-act="tier" data-tier="${d.tier}">${escapeHtml(t(d.name))}</button>`,
    ).join("");
    const rows = tableOf(c, tableTier)
      .map((r, i) => {
        const club = clubById(c.clubs, r.club);
        const pos = i + 1;
        const zone =
          tableTier === TOP_TIER && pos === 1
            ? " is-champ"
            : tableTier < TOP_TIER && pos <= MOVERS
              ? " is-up"
              : tableTier > 0 && pos > CLUBS_PER_DIVISION - MOVERS
                ? " is-down"
                : "";
        const me = club.id === c.season.club && tableTier === c.season.tier ? " is-me" : "";
        const dg = r.goalsFor - r.goalsAgainst;
        const form = r.form
          .slice(-5)
          .map((x) => `<i class="is-${x}"></i>`)
          .join("");
        return `
          <tr class="${zone}${me}">
            <td class="ch-pos">${pos}</td>
            <td class="ch-club-cell">${kitTokenHtml(kitOf(club), "", "s")}<span>${escapeHtml(club.name)}</span></td>
            <td>${r.played}</td>
            <td>${r.won}-${r.drawn}-${r.lost}</td>
            <td>${dg > 0 ? "+" : ""}${dg}</td>
            <td class="ch-pts">${r.points}</td>
            <td class="ch-form-cell"><span class="ch-form">${form}</span></td>
          </tr>`;
      })
      .join("");
    const legend = [
      tableTier === TOP_TIER ? `<span class="is-champ">■ ${escapeHtml(t("Campeón"))}</span>` : `<span class="is-up">■ ${escapeHtml(t("Suben {n}", { n: MOVERS }))}</span>`,
      tableTier > 0 ? `<span class="is-down">■ ${escapeHtml(t("Bajan {n}", { n: MOVERS }))}</span>` : "",
    ].join("");
    return `
      <div class="ch-chips" role="group" aria-label="${escapeHtml(t("División"))}">${chips}</div>
      <table class="ch-table">
        <thead><tr>
          <th>#</th><th>${escapeHtml(t("Club"))}</th><th title="${escapeHtml(t("Partidos jugados"))}">${escapeHtml(t("PJ"))}</th>
          <th title="${escapeHtml(t("Ganados, empatados, perdidos"))}">${escapeHtml(t("G-E-P"))}</th>
          <th title="${escapeHtml(t("Diferencia de goles"))}">${escapeHtml(t("DG"))}</th><th>${escapeHtml(t("Pts"))}</th><th class="ch-form-cell">${escapeHtml(t("Forma"))}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p class="ch-legend">${legend}</p>`;
  };

  const calendarTab = (c: Career): string => {
    const s = c.season;
    const items: string[] = [];
    for (let md = 0; md < MATCHDAYS; md++) {
      const game = matchdayOf(c, s.tier, md).find((g) => g.home.id === s.club || g.away.id === s.club);
      if (!game) continue;
      const home = game.home.id === s.club;
      const opp = home ? game.away : game.home;
      const line = s.lines.find((l) => l.matchday === md);
      let cls = "";
      let score = "—";
      if (game.score) {
        const [hg, ag] = game.score;
        const gf = home ? hg : ag;
        const ga = home ? ag : hg;
        score = `${gf}-${ga}`;
        cls = gf > ga ? "is-win" : gf < ga ? "is-loss" : "is-draw";
      } else if (md === s.matchday && c.phase === "season") {
        cls = "is-next";
      }
      const grade = line ? (line.absent ? t("sin ti") : line.grade.toFixed(1)) : "";
      items.push(`
        <li class="ch-cal-row ${cls}">
          <span class="ch-md">${escapeHtml(t("J{md}", { md: md + 1 }))}</span>
          <span class="ch-where">${escapeHtml(home ? t("Casa") : t("Fuera"))}</span>
          <span class="ch-cal-club">${kitTokenHtml(kitOf(opp), "", "s")}<span>${escapeHtml(opp.name)}</span></span>
          <b class="ch-score">${score}</b>
          <span class="ch-grade">${escapeHtml(grade)}</span>
        </li>`);
    }
    return `<ol class="ch-cal">${items.join("")}</ol>`;
  };

  const cardTab = (c: Career): string => {
    const attrs = ATTRIBUTE_KEYS.map((k) => {
      const v = c.player.attributes[k];
      const pct = Math.round(((v - 30) / 69) * 100);
      return `
        <div class="ch-attr">
          <span>${escapeHtml(t(ATTRIBUTE_LABEL[k]))}</span>
          <i><b style="width:${pct}%"></b></i>
          <em>${v}</em>
        </div>`;
    }).join("");
    const tt = c.totals;
    const history = [...c.history]
      .reverse()
      .map(
        (h) => `
        <tr>
          <td>T${h.number}</td>
          <td>${h.age}</td>
          <td class="ch-hist-club">${escapeHtml(h.clubName)}</td>
          <td>${escapeHtml(t(DIVISIONS[h.tier]!.name))}</td>
          <td>${ordinal(h.position)}${h.champion ? " 🏆" : h.promoted ? " ⬆️" : h.relegated ? " ⬇️" : ""}</td>
          <td>${h.goals}</td>
          <td>${h.grade.toFixed(1)}</td>
        </tr>`,
      )
      .join("");
    const kits = c.earnedKits
      .map((id) => `<span class="ch-kit" title="${escapeHtml(KITS[id] ? t(KITS[id]!.label) : id)}">${kitTokenHtml(KITS[id], "", "s")}</span>`)
      .join("");
    return `
      <form class="ch-rename" data-rename>
        <label>
          <span>${escapeHtml(t("Nombre en la camiseta"))}</span>
          <input type="text" data-rename-input maxlength="16" autocomplete="off" spellcheck="false" value="${escapeHtml(c.player.name)}" />
        </label>
        <button type="submit" class="ch-btn">${escapeHtml(t("Cambiar"))}</button>
      </form>
      <div class="ch-attrs">${attrs}</div>
      <div class="ch-tiles ch-tiles--5">
        <div><b>${tt.matches}</b><span>${escapeHtml(t("Partidos"))}</span></div>
        <div><b>${tt.goals}</b><span>${escapeHtml(t("Goles"))}</span></div>
        <div><b>${tt.assists}</b><span>${escapeHtml(t("Asistencias"))}</span></div>
        <div><b>${tt.mvps}</b><span>MVP</span></div>
        <div><b>${tt.wins}-${tt.draws}-${tt.losses}</b><span>${escapeHtml(t("G-E-P"))}</span></div>
      </div>
      ${
        c.titles.length
          ? `<h3 class="ch-sub">${escapeHtml(t("Títulos"))}</h3><ul class="ch-titles">${c.titles
              .map((x) => `<li>🏆 ${escapeHtml(t(DIVISIONS[x.tier]!.name))} · ${escapeHtml(x.clubName)} · T${x.season}</li>`)
              .join("")}</ul>`
          : ""
      }
      <h3 class="ch-sub">${escapeHtml(t("Temporadas"))}</h3>
      ${
        history
          ? `<table class="ch-table ch-hist"><thead><tr><th>${escapeHtml(t("Temp."))}</th><th>${escapeHtml(t("Edad"))}</th><th>${escapeHtml(t("Club"))}</th><th>${escapeHtml(t("División"))}</th><th>${escapeHtml(t("Pos."))}</th><th>${escapeHtml(t("Goles"))}</th><th>${escapeHtml(t("Nota"))}</th></tr></thead><tbody>${history}</tbody></table>`
          : `<p class="ch-empty">${escapeHtml(t("Aún ninguna. La primera siempre es la más difícil de olvidar."))}</p>`
      }
      <h3 class="ch-sub">${escapeHtml(t("Camisetas de esta carrera"))}</h3>
      ${kits ? `<div class="ch-kits">${kits}</div>` : `<p class="ch-empty">${escapeHtml(t("Ninguna todavía. Acaba una temporada y la de tu club es tuya."))}</p>`}`;
  };

  const tabBody = (c: Career): string => {
    if (tab === "table") return tableTab(c);
    if (tab === "calendar") return calendarTab(c);
    if (tab === "card") return cardTab(c);
    return phoneTab(c);
  };

  const careerView = (c: Career): string => {
    const unread = phoneUnread(c, seenMap());
    const labels: Record<Tab, string> = {
      phone: t("Móvil"),
      table: t("Tabla"),
      calendar: t("Calendario"),
      card: t("Ficha"),
    };
    const tabs = TABS.map(
      (id) => `
        <button type="button" role="tab" class="ch-tab${id === tab ? " is-on" : ""}" aria-selected="${id === tab}" data-act="tab" data-tab="${id}">
          ${escapeHtml(labels[id])}${id === "phone" && unread > 0 && (tab !== "phone" || phoneUi.thread !== null) ? `<i class="ch-badge">${unread}</i>` : ""}
        </button>`,
    ).join("");
    const main =
      c.phase === "retired" ? retiredCard(c) : c.phase === "offseason" ? offseasonCard(c) : nextCard(c);
    return `
      ${topbar(
        t("Carrera · Temporada {n}", { n: c.season.number }),
        `<button type="button" class="ch-ghost" data-act="hall">🏛️ ${escapeHtml(t("Salón de la fama"))}</button>
         ${c.phase === "retired" ? "" : `<button type="button" class="ch-ghost" data-act="new">${escapeHtml(t("Nueva carrera"))}</button>`}`,
      )}
      <div class="ch-main">
        <div class="ch-left">
          ${meCard(c)}
          ${main}
        </div>
        <div class="ch-right">
          <nav class="ch-tabs" role="tablist">${tabs}</nav>
          <div class="ch-tab-body" data-tab-body>${tabBody(c)}</div>
        </div>
      </div>`;
  };

  const createView = (d: CreateDraft): string => {
    const clubs = startingClubs(d.seed).sort((a, b) => Number(b.name === "Ytuano FC") - Number(a.name === "Ytuano FC"));
    const positions = (["DEL", "MED", "DEF"] as const)
      .map(
        (p) => `
        <button type="button" class="ch-pick${d.position === p ? " is-on" : ""}" data-act="pos" data-pos="${p}" aria-pressed="${d.position === p}">
          <b>${escapeHtml(t(POSITION_LABEL[p]))}</b><small>${escapeHtml(t(POSITION_BLURB[p]))}</small>
        </button>`,
      )
      .join("");
    const feet = (["right", "left"] as const)
      .map(
        (f) => `
        <button type="button" class="ch-pick ch-pick--small${d.foot === f ? " is-on" : ""}" data-act="foot" data-foot="${f}" aria-pressed="${d.foot === f}">
          <b>${escapeHtml(t(FOOT_LABEL[f]))}</b><small>${escapeHtml(f === "right" ? t("Juegas por la derecha") : t("Juegas por la izquierda"))}</small>
        </button>`,
      )
      .join("");
    const clubCards = clubs
      .map(
        (club) => `
        <button type="button" class="ch-club${d.clubId === club.id ? " is-on" : ""}" data-act="club" data-club="${club.id}" aria-pressed="${d.clubId === club.id}">
          ${kitTokenHtml(kitOf(club), d.number, "m")}
          <span class="ch-club-body">
            <b>${escapeHtml(club.name)}</b>
            <span class="ch-club-meta">${escapeHtml(t(STYLE_LABEL[club.style]))} · 🏟️ ${escapeHtml(club.stadium.name)}</span>
            <span class="ch-club-quirk">${escapeHtml(t(club.quirk))}</span>
          </span>
        </button>`,
      )
      .join("");
    return `
      ${topbar(t("Nueva carrera"))}
      <div class="ch-main ch-create">
        <div class="ch-left ch-form">
          <p class="ch-lede">${escapeHtml(t("Tienes 17 años, unas botas prestadas y un sueño. Las botas hay que devolverlas."))}</p>
          <div class="ch-row">
          <label class="ch-field">
            <span>${escapeHtml(t("Nombre"))}</span>
            <input type="text" data-field="name" maxlength="16" autocomplete="off" spellcheck="false" value="${escapeHtml(d.name)}" placeholder="${escapeHtml(t("Cómo te van a corear"))}" />
          </label>
          <label class="ch-field ch-field--num">
            <span>${escapeHtml(t("Dorsal"))}</span>
            <input type="number" data-field="number" min="1" max="99" inputmode="numeric" value="${d.number}" />
          </label>
          </div>
          <div class="ch-group"><span class="ch-group-label">${escapeHtml(t("Posición"))}</span><div class="ch-picks ch-picks--3">${positions}</div></div>
          <div class="ch-group"><span class="ch-group-label">${escapeHtml(t("Pie bueno"))}</span><div class="ch-picks ch-picks--2">${feet}</div></div>
          ${createError ? `<p class="ch-error" role="alert">${escapeHtml(createError)}</p>` : ""}
          <button type="button" class="ch-play" data-act="start">${escapeHtml(t("EMPEZAR CARRERA"))}</button>
        </div>
        <div class="ch-right">
          <h2 class="ch-sub ch-sub--top">${escapeHtml(t("Elige tu primer club · Liga del Barrio"))}</h2>
          <div class="ch-clubs">${clubCards}</div>
        </div>
      </div>`;
  };

  const hallView = (): string => {
    const hall = loadHallOfFame();
    const cards = hall
      .map((e: HallOfFameEntry) => {
        const primera = e.titles.filter((x) => x.tier === TOP_TIER).length;
        return `
          <li class="ch-card ch-bust">
            <header>
              <strong>${escapeHtml(e.name)}</strong>
              <span>#${e.number} · ${escapeHtml(t(POSITION_SHORT[e.position as Position] ?? e.position))} · ${escapeHtml(t("17 a {n} años", { n: e.lastAge }))}</span>
            </header>
            <p class="ch-epitaph">«${escapeHtml(t(e.verdict))}»</p>
            <div class="ch-tiles ch-tiles--5">
              <div><b>${e.seasons}</b><span>${escapeHtml(t("Temporadas"))}</span></div>
              <div><b>${e.matches}</b><span>${escapeHtml(t("Partidos"))}</span></div>
              <div><b>${e.goals}</b><span>${escapeHtml(t("Goles"))}</span></div>
              <div><b>${e.titles.length}</b><span>${escapeHtml(t("Títulos"))}</span></div>
              <div><b>${e.peakOverall}</b><span>${escapeHtml(t("Mejor media"))}</span></div>
            </div>
            <p class="ch-bust-path">${e.clubs.map(escapeHtml).join(" → ")}</p>
            ${primera ? `<p class="ch-bust-gold">🏆 ${escapeHtml(t("{n} ligas de Primera", { n: primera }))}</p>` : ""}
          </li>`;
      })
      .join("");
    return `
      ${topbar(t("Salón de la fama"))}
      <div class="ch-main ch-hall">
        ${
          cards
            ? `<ol class="ch-busts">${cards}</ol>`
            : `<p class="ch-empty ch-empty--big">${escapeHtml(t("Aún no hay nadie. El primero en retirarse se queda con la mejor plaza. Es la única."))}</p>`
        }
      </div>`;
  };

  const render = (): void => {
    overlay.dataset.view = view;
    if (view === "create" && draft) overlay.innerHTML = createView(draft);
    else if (view === "hall") overlay.innerHTML = hallView();
    else if (career) overlay.innerHTML = careerView(career);
    flash = [];
    // Abrir una conversacion es leerla (la bandeja no: se lee entrando).
    if (view === "career" && career && tab === "phone" && phoneUi.thread && phoneUi.typing === null) {
      const thread = phoneUi.thread;
      const last = career.feed.filter((m) => m.from === thread).at(-1)?.id ?? 0;
      markThreadSeen(thread, last);
      // El chat, abajo del todo: lo ultimo es lo que se lee.
      const chat = overlay.querySelector<HTMLElement>("[data-chat]");
      if (chat) chat.scrollTop = chat.scrollHeight;
    }
  };

  const focusMain = (): void => {
    const target =
      overlay.querySelector<HTMLElement>("[data-act='play']") ??
      overlay.querySelector<HTMLElement>("[data-act='sign'], [data-act='stay'], [data-act='retire']") ??
      overlay.querySelector<HTMLElement>("[data-act='back']");
    target?.focus({ preventScroll: true });
  };

  const openCreate = (): void => {
    draft = { seed: newSeed(), name: defaultName(), number: career?.player.number ?? 10, position: "DEL", foot: "right", clubId: null };
    createError = "";
    view = "create";
    render();
    const nameInput = overlay.querySelector<HTMLInputElement>("[data-field='name']");
    nameInput?.focus({ preventScroll: true });
    // Viene relleno con tu apodo y el cursor quedaba al principio: lo que
    // escribias se pegaba delante ("luciaEl Tuercas"). Seleccionado, escribir
    // lo sustituye, y si te vale, no tocas nada.
    nameInput?.select();
  };

  // ------------------------------------------------------------ acciones

  const act = async (el: HTMLElement): Promise<void> => {
    const what = el.dataset.act;
    if (!what || busy) return;
    const c = career;

    if (what === "back") {
      if (view !== "career" && c) {
        view = "career";
        render();
        focusMain();
      } else {
        finish({ type: "back" });
      }
      return;
    }
    if (what === "play") {
      if (c && nextFixture(c)) finish({ type: "play" });
      return;
    }
    if (what === "thread" || what === "inbox") {
      window.clearTimeout(typingTimer);
      const thread = what === "thread" ? ((el.dataset.thread as ThreadId | undefined) ?? null) : null;
      chatSeenBefore = thread ? (seenMap()[thread] ?? 0) : 0;
      phoneUi = { thread, typing: null, effect: null };
      render();
      // Apilado en movil, la conversacion puede quedar a media pantalla: arriba.
      if (thread) overlay.querySelector(".ph--chat")?.scrollIntoView({ block: "start" });
      overlay
        .querySelector<HTMLElement>(thread ? ".ph-reply, [data-act='inbox']" : `[data-thread="${el.dataset.from ?? ""}"], .ph-thread`)
        ?.focus({ preventScroll: true });
      return;
    }
    if (what === "reply") {
      if (!c || !phoneUi.thread) return;
      const res = answerMessage(c, Number(el.dataset.msg), Number(el.dataset.opt));
      if (res.career === c) return;
      commit(res.career);
      // Tu burbuja sale ya; la suya, tras un "escribiendo…".
      const theirs = res.career.feed.at(-1)!.id;
      chatSeenBefore = theirs - 1;
      phoneUi = { thread: phoneUi.thread, typing: theirs, effect: null };
      render();
      const chat = overlay.querySelector<HTMLElement>("[data-chat]");
      if (chat) chat.scrollTop = chat.scrollHeight;
      window.clearTimeout(typingTimer);
      typingTimer = window.setTimeout(() => {
        if (!phoneUi.thread) return;
        phoneUi = { thread: phoneUi.thread, typing: null, effect: res.effect };
        render();
        overlay.querySelector<HTMLElement>(".ph-reply, [data-act='inbox']")?.focus({ preventScroll: true });
      }, 1100);
      return;
    }
    if (what === "tab") {
      phoneUi = { thread: null, typing: null, effect: null };
      tab = (el.dataset.tab as Tab) ?? "phone";
      writeStored(TAB_KEY, tab);
      if (tab === "table" && c) tableTier = c.season.tier;
      render();
      overlay.querySelector<HTMLElement>(`[data-tab="${tab}"]`)?.focus({ preventScroll: true });
      return;
    }
    if (what === "tier") {
      tableTier = Number(el.dataset.tier) || 0;
      render();
      overlay.querySelector<HTMLElement>(`[data-tier="${tableTier}"]`)?.focus({ preventScroll: true });
      return;
    }
    if (what === "hall") {
      view = "hall";
      render();
      overlay.querySelector<HTMLElement>("[data-act='back']")?.focus({ preventScroll: true });
      return;
    }
    if (what === "sign" || what === "stay") {
      if (!c) return;
      const step = acceptOffer(c, what === "sign" ? el.dataset.club ?? null : null);
      commit(step.career, step.events);
      tab = "phone";
      render();
      focusMain();
      return;
    }
    if (what === "retire") {
      if (!c) return;
      busy = true;
      const ok = mustRetire(c)
        ? true
        : await confirmDialog({
            title: t("¿Colgar las botas?"),
            body: t("Se acabó: entras en el salón de la fama y no hay vuelta atrás. Tus equipaciones se quedan."),
            confirm: t("Colgarlas"),
            cancel: t("Una más"),
          });
      busy = false;
      if (!ok) return;
      const done = retire(c, Date.now());
      addToHallOfFame(done.entry);
      commit(done.career, done.events);
      render();
      focusMain();
      return;
    }
    if (what === "new") {
      if (c && c.phase !== "retired") {
        busy = true;
        const ok = await confirmDialog({
          title: t("¿Empezar otra carrera?"),
          body: t("La de ahora se pierde para siempre y no entra en el salón de la fama. Las equipaciones que ganaste se quedan."),
          confirm: t("Empezar otra"),
          cancel: t("Mejor no"),
        });
        busy = false;
        if (!ok) return;
      }
      openCreate();
      return;
    }
    if (!draft) return;
    if (what === "pos") draft.position = el.dataset.pos as Position;
    else if (what === "foot") draft.foot = el.dataset.foot as Foot;
    else if (what === "club") draft.clubId = el.dataset.club ?? null;
    else if (what === "start") {
      const name = draft.name.trim();
      if (!name) createError = t("Ponte un nombre. «El chaval» no sale bien en las camisetas.");
      else if (!draft.clubId) createError = t("Elige un club. Ninguno te va a venir a buscar.");
      else {
        const fresh = newCareer({
          seed: draft.seed,
          name,
          number: draft.number,
          position: draft.position,
          foot: draft.foot,
          clubId: draft.clubId,
        });
        // Mensajes nuevos: los de la bienvenida se leen como nuevos.
        writeStored(SEEN_KEY, "{}");
        phoneUi = { thread: null, typing: null, effect: null };
        draft = null;
        commit(fresh);
        view = "career";
        tab = "phone";
        render();
        focusMain();
        return;
      }
    } else return;
    if (what !== "start") createError = "";
    const scroll = overlay.querySelector(".ch-clubs")?.scrollTop ?? 0;
    render();
    const clubs = overlay.querySelector(".ch-clubs");
    if (clubs) clubs.scrollTop = scroll;
    overlay.querySelector<HTMLElement>(`[data-act="${what}"][aria-pressed="true"], [data-act="${what}"]`)?.focus({ preventScroll: true });
  };

  overlay.addEventListener("click", (event) => {
    const el = (event.target as HTMLElement).closest<HTMLElement>("[data-act]");
    if (el && overlay.contains(el)) void act(el);
  });
  // Cambiar el nombre desde la ficha (Enter o el boton).
  overlay.addEventListener("submit", (event) => {
    const form = event.target as HTMLElement;
    if (!form.matches("[data-rename]")) return;
    event.preventDefault();
    const input = form.querySelector<HTMLInputElement>("[data-rename-input]");
    if (!career || !input) return;
    const next = renamePlayer(career, input.value);
    if (next === career) return;
    commit(next);
    flash.push(`✏️ ${escapeHtml(t("Ahora te corean como {name}.", { name: next.player.name }))}`);
    render();
  });
  overlay.addEventListener("input", (event) => {
    const input = event.target as HTMLInputElement;
    if (!draft) return;
    if (input.dataset.field === "name") draft.name = input.value.slice(0, 16);
    if (input.dataset.field === "number") {
      const n = Math.round(Number(input.value));
      if (Number.isFinite(n) && n >= 1 && n <= 99) {
        draft.number = n;
        // El dorsal sale en las fichas de los clubes: se ve como quedaria.
        for (const num of overlay.querySelectorAll(".ch-club .kit-token-num")) num.textContent = String(n);
      }
    }
  });

  const onKey = (event: KeyboardEvent): void => {
    if (overlay.classList.contains("is-hidden") || event.key !== "Escape") return;
    const target = event.target as HTMLElement | null;
    if (target?.tagName === "INPUT") {
      target.blur();
      return;
    }
    event.preventDefault();
    const back = overlay.querySelector<HTMLElement>("[data-act='back']");
    if (back) void act(back);
  };
  window.addEventListener("keydown", onKey);

  function hide(): void {
    overlay.classList.add("is-hidden");
  }

  return {
    show(opts = {}) {
      career = loadCareer();
      flash = [];
      if (career) {
        // Se cerro el juego a mitad de partido: se jugo sin ti.
        const leftover = resolvePending(career);
        if (leftover) {
          const lost = leftover.career.season.lines.at(-1);
          commit(leftover.career, leftover.events);
          if (lost?.absent) flash.push(`⚠️ ${escapeHtml(t("Cerraste a mitad de partido: la jornada se jugó sin ti."))}`);
        } else {
          // Por si algo se gano con una version sin almacen de equipaciones.
          commit(career);
        }
        tableTier = career.season.tier;
      }
      overlay.classList.remove("is-hidden");
      if (!career || opts.create) {
        openCreate();
      } else {
        view = "career";
        // Lo nuevo del movil, lo primero (en la bandeja: se entra al chat que toque).
        phoneUi = { thread: null, typing: null, effect: null };
        if (phoneUnread(career, seenMap()) > 0) tab = "phone";
        render();
        focusMain();
      }
      return new Promise((resolve) => {
        resolveShow = resolve;
      });
    },
    refresh() {
      if (overlay.classList.contains("is-hidden") || view !== "career") return;
      const fresh = loadCareer();
      if (!fresh) return;
      career = fresh;
      tableTier = fresh.season.tier;
      flash.push(`☁️ ${escapeHtml(t("Tu carrera se ha puesto al día con la de tu cuenta."))}`);
      render();
    },
    hide,
    leave: () => finish({ type: "back" }),
    dispose() {
      window.removeEventListener("keydown", onKey);
      overlay.remove();
    },
  };
}
